import React from "react";
import axios from "axios";
import Head from "next/head";
import ErrorMessage from "@/components/ErrorMessage";
import TextFieldLabel from "@/components/TextFieldLabel";
import { useForm } from "react-hook-form";
import { UserLayout } from "@/components/Layout";
import { useTranslation } from "next-i18next";
import { NextPageWithLayout } from "@/pages/_app";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { toast } from "react-toastify";
import { TOAST_ERROR, TOAST_SUCCESS } from "@/styles/toast";

const ContactPage: NextPageWithLayout = () => {
  const { t } = useTranslation(["contact", "form", "pageTitle"]);
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm();

  const onSubmit = handleSubmit((data, e) => {
    axios
      .post("/contact-form", data)
      .then((res) => {
        toast.success(res.data.message, TOAST_SUCCESS);
        e?.target.reset();
      })
      .catch((error) => {
        toast.error(error.response.data.error, TOAST_ERROR);
      });
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  });

  return (
    <>
      <Head>
        <title>{`${t("contactPage", { ns: "pageTitle" })} ${t("baseTitle", {
          ns: "pageTitle",
        })}`}</title>
      </Head>
      <div className="flex h-full flex-col md:items-center md:justify-between lg:flex-row">
        <div className="mx-auto md:w-3/4 lg:w-1/2 lg:max-w-lg">
          <h2 className="text-3xl font-bold md:text-4xl">{t("title")}</h2>
          <p className="mt-4 lg:text-lg">{t("description")}</p>
          <p className="mt-4 lg:text-lg">{t("partnerships")}</p>
          <button className="mt-8 w-fit whitespace-nowrap rounded-md border border-transparent bg-primary px-4 py-2 text-base font-medium text-gray-100 shadow-sm">
            <a href="/discord">Join Discord</a>
          </button>
        </div>
        <form
          onSubmit={onSubmit}
          className="mt-12 space-y-8 rounded-xl bg-card px-6 py-8 shadow-lg md:mx-auto md:w-3/4 lg:mt-0 lg:w-1/2 lg:max-w-lg"
        >
          <div>
            <TextFieldLabel
              type="text"
              displayLabel={t("label.name", { ns: "form" })}
              label="name"
              bgColor="bg-card"
              register={register}
              required
            />
            <ErrorMessage>
              {errors.name?.type === "required" && "Name required"}
            </ErrorMessage>
          </div>

          <div>
            <TextFieldLabel
              type="text"
              label="email"
              displayLabel={t("label.email", { ns: "form" })}
              bgColor="bg-card"
              register={register}
              required
            />
            <ErrorMessage>
              {errors.email?.type === "required" && "Email required"}
            </ErrorMessage>
            <ErrorMessage>
              {errors.email?.type === "pattern" && "Invalid email"}
            </ErrorMessage>
          </div>

          <div>
            <TextFieldLabel
              type="text"
              displayLabel={t("label.subject", { ns: "form" })}
              label="subject"
              bgColor="bg-card"
              register={register}
              required
            />
            <ErrorMessage>
              {errors.subject?.type === "required" && "Subject required"}
            </ErrorMessage>
          </div>

          <div>
            <TextFieldLabel
              type="textarea"
              displayLabel={t("label.message", { ns: "form" })}
              label="message"
              bgColor="bg-card"
              register={register}
              required
              rows={8}
            />
            <ErrorMessage>
              {errors.message?.type === "required" && "Message required"}
            </ErrorMessage>
          </div>

          <button className="mt-4 w-fit whitespace-nowrap rounded-md border border-transparent bg-primary px-4 py-2 text-sm font-normal text-gray-100 shadow-sm">
            {t("action.submit", { ns: "form" })}
          </button>
        </form>
      </div>
    </>
  );
};

export async function getStaticProps(props: { locale: string }) {
  return {
    props: {
      ...(await serverSideTranslations(props.locale, [
        "contact",
        "common",
        "form",
        "pageTitle",
      ])),
    },
  };
}

ContactPage.getLayout = UserLayout;

export default ContactPage;
